import {
  Alert,
  Button,
  Flex,
  Heading,
  IconAddLine,
  Spinner,
  TextArea,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useOutletContext } from "react-router-dom";
import ActionTypesModal from "../../../../../components/Action/ActionTypesModal";
import {
  ERROR,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import ProgressStepper from "../../../../../components/ProgressStepper";
import { MULTI, Selector } from "../../../../../components/Selectors/Selector";
import { ActionService } from "../../../../../services/ActionService";
import { UserDetailContext } from "../../../../../utils/Context";
import { getFinalizeSteps } from "../utils";
import ActionCard from "./ActionCard";

const FinalizeActions = () => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const { actions, setActions, actionTypes, setActionTypes } =
    useOutletContext();
  const location = useLocation();
  const section = location.state.section;
  const [creatingAction, setCreatingAction] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState("");
  const [actionTypeClear, setActionTypeClear] = useState(false);
  const [actionText, setActionText] = useState("");
  const [authorsFilters, setAuthorsFilters] = useState([]);
  const [actionTypeFilters, setActionTypeFilters] = useState([]);
  const [addActionTypeOpen, setActionTypeOpen] = useState(false);

  const authorSelections = actions
    ? Array.from(new Set(actions.map((a) => a.author))).map((author) => ({
        label: author,
        id: author,
      }))
    : [];

  useEffect(() => {
    setActionTypeClear(false);
  }, [actionTypeClear]);

  function handleActionTypeSelect(actionTypeId) {
    setSelectedActionType(actionTypes.find((a) => a.id === actionTypeId));
  }

  async function handleCreateAction() {
    if (selectedActionType && actionText) {
      try {
        setCreatingAction(true);
        const action = {
          action_type: {
            id: selectedActionType.id,
            name: selectedActionType.name,
          },
          text: actionText,
          reflections: [],
          author: userDetails.name,
        };
        const newAction = await ActionService.createActionForContext(
          action,
          section.id
        );
        setActions([newAction, ...actions]);
        setActionText("");
        setSelectedActionType("");
        setActionTypeClear(true);
        showNotification(
          t("notifications.general.createSuccess", {
            obj: t("notifications.action.obj"),
          })
        );
      } catch {
        showNotification(
          t("notifications.general.createError", {
            obj: t("notifications.action.obj"),
          }),
          ERROR
        );
      } finally {
        setCreatingAction(false);
      }
    } else {
      showNotification(t("actions.addActionError"), ERROR);
    }
  }

  function handleActionTextChange(e) {
    setActionText(e.target.value);
  }

  function handleAuthorSelect(selectedAuthors) {
    setAuthorsFilters(selectedAuthors);
  }

  function handleActionTypesSelect(selectedTypes) {
    setActionTypeFilters(selectedTypes);
  }

  function isFiltered(action) {
    if (
      authorsFilters.length > 0 &&
      !authorsFilters.some((authorName) => authorName === action.author)
    ) {
      return false;
    }
    if (
      actionTypeFilters.length > 0 &&
      !actionTypeFilters.some((typeId) => typeId === action.action_type.id)
    ) {
      return false;
    }
    return true;
  }

  const filteredActions = actions?.filter((action) => isFiltered(action));

  const updateActionTypes = (actionTypeList) => {
    setActionTypes(
      actionTypeList.map((actionType) => ({
        id: actionType.id,
        label: actionType.name,
      }))
    );
  };

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <ProgressStepper steps={getFinalizeSteps(t)} currentStep={3} />
      <div style={{ flex: 1, overflowY: "hidden" }}>
        <Flex gap="small" height="100%">
          <Flex.Item size="50%" height="100%">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                height: "100%",
              }}
            >
              <Heading level="h3" as="h2" border="bottom" margin="0 small">
                {t("finalizeCourse.createActions")}
              </Heading>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                  overflowY: "auto",
                  padding: "0 0.75rem",
                }}
              >
                <Flex gap="medium" wrap="wrap">
                  <Flex.Item shouldGrow>
                    <Selector
                      label={t("actions.actionType")}
                      id="action-type-select"
                      options={actionTypes}
                      selectHandler={handleActionTypeSelect}
                      selectedItemId={selectedActionType.id}
                      clear={actionTypeClear}
                    />
                  </Flex.Item>
                  <Flex.Item align="end">
                    <Button
                      renderIcon={IconAddLine}
                      onClick={() => setActionTypeOpen(true)}
                    >
                      {t("actions.addActionType")}
                    </Button>
                    <ActionTypesModal
                      addTypesModalOpen={addActionTypeOpen}
                      setAddTypesModalOpen={setActionTypeOpen}
                      handleCloseTypesModal={() => setActionTypeOpen(false)}
                      setTypes={updateActionTypes}
                    />
                  </Flex.Item>
                </Flex>
                <TextArea
                  label={t("actions.action")}
                  value={actionText}
                  onChange={handleActionTextChange}
                />
                <div style={{ alignSelf: "end", paddingBottom: "0.75rem" }}>
                  <Button
                    color="primary"
                    renderIcon={
                      creatingAction ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconAddLine
                      )
                    }
                    disabled={creatingAction}
                    onClick={handleCreateAction}
                  >
                    {t("academicHierarchy.create")}
                  </Button>
                </div>
              </div>
            </div>
          </Flex.Item>
          <Flex.Item height="100%">
            <div style={{ height: "100%", borderRight: "1px solid #ccc" }} />
          </Flex.Item>
          <Flex.Item shouldGrow shouldShrink height="100%">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                height: "100%",
              }}
            >
              <Heading level="h3" as="h2" border="bottom" margin="0 small">
                {t("finalizeCourse.existingActions")}
              </Heading>
              <div
                style={{ display: "flex", gap: "1.5rem", padding: "0 0.75rem" }}
              >
                <div style={{ width: "50%" }}>
                  <Selector
                    type={MULTI}
                    label={t("finalizeCourse.authors")}
                    options={authorSelections}
                    selectHandler={handleAuthorSelect}
                    selectedOptions={authorsFilters}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <Selector
                    type={MULTI}
                    label={t("actions.actionType")}
                    options={actionTypes}
                    selectHandler={handleActionTypesSelect}
                    selectedOptions={actionTypeFilters}
                  />
                </div>
              </div>
              <div style={{ padding: "0.75rem", overflowY: "auto" }}>
                {actions ? (
                  actions.length > 0 ? (
                    filteredActions.length > 0 ? (
                      <Flex wrap="wrap" gap="medium">
                        {filteredActions.map((action) => (
                          <Flex.Item key={action.id} size="100%">
                            <ActionCard action={action} />
                          </Flex.Item>
                        ))}
                      </Flex>
                    ) : (
                      <Alert>{t("finalizeCourse.noActionsFound")}</Alert>
                    )
                  ) : (
                    <Alert>{t("finalizeCourse.createAnAction")}</Alert>
                  )
                ) : (
                  <>
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                  </>
                )}
              </div>
            </div>
          </Flex.Item>
        </Flex>
      </div>
    </div>
  );
};

export default FinalizeActions;
